import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { cn } from '../lib/utils';

export type AutoExpandTextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'rows'
> & {
  value: string;
  onChange: (value: string) => void;
  minRows?: number;
  maxRows?: number;
};

const AutoExpandTextarea = React.forwardRef<HTMLTextAreaElement, AutoExpandTextareaProps>(
  ({ value, onChange, minRows = 1, maxRows, className, style, ...rest }, forwardedRef) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );

    const adjustHeight = useCallback(() => {
      const el = innerRef.current;
      if (!el) return;

      const computedStyle = window.getComputedStyle(el);
      const lineHeight = Number.parseFloat(computedStyle.lineHeight) || 20;
      const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;
      const borderTop = Number.parseFloat(computedStyle.borderTopWidth) || 0;
      const borderBottom = Number.parseFloat(computedStyle.borderBottomWidth) || 0;
      const verticalChrome = paddingTop + paddingBottom + borderTop + borderBottom;
      const minHeight = (lineHeight * minRows) + verticalChrome;
      const maxHeight = maxRows != null ? (lineHeight * maxRows) + verticalChrome : null;

      el.style.height = 'auto';
      const nextHeight = Math.max(el.scrollHeight, minHeight);
      if (maxHeight != null && nextHeight > maxHeight) {
        el.style.height = `${maxHeight}px`;
        el.style.overflowY = 'auto';
        return;
      }
      el.style.height = `${nextHeight}px`;
      el.style.overflowY = 'hidden';
    }, [maxRows, minRows]);

    useLayoutEffect(() => {
      adjustHeight();
    }, [value, adjustHeight]);

    return (
      <textarea
        {...rest}
        ref={setRefs}
        rows={minRows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden resize-none',
          className,
        )}
        style={style}
      />
    );
  }
);

AutoExpandTextarea.displayName = 'AutoExpandTextarea';

export default AutoExpandTextarea;
